import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { X, RefreshCw, Shield, Clock, Key, AlertTriangle } from 'lucide-react'
import type { McpConnectionEntry } from '../../engine/mcp/types'
import './McpOAuthStateInspector.css'

// ── Response types from GET /mcp/oauth-state ──

interface OAuthSessionSummary {
    sessionId: string
    configBlobId: string
    redirectUri: string
    hasAuthCode: boolean
    challengeExpiry: string
    challengeFreshness: 'fresh' | 'expired'
    state: string
}

interface OAuthStateResponse {
    activeSessionCount: number
    sessions: OAuthSessionSummary[]
}

function isOAuthSessionSummary(value: unknown): value is OAuthSessionSummary {
    if (!value || typeof value !== 'object') return false
    const candidate = value as Partial<OAuthSessionSummary>
    return typeof candidate.sessionId === 'string'
        && typeof candidate.configBlobId === 'string'
        && typeof candidate.redirectUri === 'string'
        && typeof candidate.hasAuthCode === 'boolean'
        && typeof candidate.challengeExpiry === 'string'
        && (candidate.challengeFreshness === 'fresh' || candidate.challengeFreshness === 'expired')
        && typeof candidate.state === 'string'
}

function isOAuthStateResponse(value: unknown): value is OAuthStateResponse {
    if (!value || typeof value !== 'object') return false
    const candidate = value as Partial<OAuthStateResponse>
    return typeof candidate.activeSessionCount === 'number'
        && Array.isArray(candidate.sessions)
        && candidate.sessions.every(isOAuthSessionSummary)
}

interface Props {
    isOpen: boolean
    onClose: () => void
    connections: McpConnectionEntry[]
}

/** §265: MCP OAuth State Inspector — surfaces active OAuth session state
 *  for all configured MCP configs, giving agents and developers a first-class
 *  OAuth health window instead of requiring raw /mcp/status or DevTools. */
export default function McpOAuthStateInspector({ isOpen, onClose, connections }: Props) {
    const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading')
    const [data, setData] = useState<OAuthStateResponse | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [copiedId, setCopiedId] = useState<string | null>(null)

    const fetchState = useCallback(async () => {
        setState('loading')
        setError(null)
        try {
            const res = await fetch('/mcp/oauth-state')
            if (!res.ok) {
                const text = await res.text().catch(() => '')
                let message: string
                try {
                    const body = JSON.parse(text)
                    message = body.error ?? `Failed to load OAuth state (${res.status})`
                } catch {
                    message = `Failed to load OAuth state (${res.status})`
                }
                throw new Error(message)
            }
            let json: unknown
            try {
                json = await res.json()
            } catch {
                throw new Error('Server returned an invalid response. Please retry.')
            }
            if (!isOAuthStateResponse(json)) {
                throw new Error('Server returned an invalid response. Please retry.')
            }
            setData(json)
            setState('loaded')
        } catch (err) {
            setState('error')
            setError(err instanceof Error ? err.message : 'Failed to load OAuth state')
        }
    }, [])

    useEffect(() => {
        if (!isOpen) return
        fetchState()
    }, [isOpen, fetchState])

    // Escape key
    useEffect(() => {
        if (!isOpen) return
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [isOpen, onClose])

    const handleCopyJson = useCallback(() => {
        if (!data) return
        navigator.clipboard.writeText(JSON.stringify(data, null, 2))
        setCopiedId('json')
        setTimeout(() => setCopiedId(null), 1500)
    }, [data])

    const handleCopySessionId = useCallback((sessionId: string) => {
        navigator.clipboard.writeText(sessionId)
        setCopiedId(sessionId)
        setTimeout(() => setCopiedId(null), 1500)
    }, [])

    const lookupLabel = (configBlobId: string): string => {
        const conn = connections.find(c => c.configBlobId === configBlobId)
        return conn?.label ?? configBlobId.slice(0, 12) + '…'
    }

    if (!isOpen) return null

    return createPortal(
        <div className="mcp-oauth-overlay" onClick={onClose}>
            <div className="mcp-oauth-modal" onClick={e => e.stopPropagation()}>
                <div className="mcp-oauth-header">
                    <div className="mcp-oauth-title-row">
                        <Shield size={18} className="mcp-oauth-icon" />
                        <span className="mcp-oauth-title">MCP OAuth State Inspector</span>
                    </div>
                    <button className="mcp-oauth-close" onClick={onClose} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <div className="mcp-oauth-body">
                    {/* Summary bar */}
                    <div className="mcp-oauth-summary">
                        <span className="mcp-oauth-count">
                            {data?.activeSessionCount ?? '—'} active session{data?.activeSessionCount === 1 ? '' : 's'}
                        </span>
                        <div className="mcp-oauth-actions">
                            <button className="mcp-oauth-action-btn" onClick={fetchState} title="Refresh">
                                <RefreshCw size={14} />
                            </button>
                            <button className="mcp-oauth-action-btn" onClick={handleCopyJson}
                                title="Copy JSON for agent consumption"
                                disabled={!data}>
                                {copiedId === 'json' ? 'Copied!' : 'Copy JSON'}
                            </button>
                        </div>
                    </div>

                    {/* Loading */}
                    {state === 'loading' && (
                        <div className="mcp-oauth-loading">
                            <div className="mcp-oauth-spinner" />
                            <p>Loading OAuth state…</p>
                        </div>
                    )}

                    {/* Error */}
                    {state === 'error' && (
                        <div className="mcp-oauth-error">
                            <AlertTriangle size={16} />
                            <p>{error}</p>
                            <button className="mcp-oauth-retry-btn" onClick={fetchState}>Retry</button>
                        </div>
                    )}

                    {/* Loaded — sessions table */}
                    {state === 'loaded' && data && (
                        data.sessions.length === 0 ? (
                            <div className="mcp-oauth-empty">
                                <Shield size={24} />
                                <p>No active OAuth sessions.</p>
                                <p className="mcp-oauth-empty-hint">
                                    Sessions appear here when an MCP client initiates an authorize flow.
                                </p>
                            </div>
                        ) : (
                            <div className="mcp-oauth-table-wrapper">
                                <table className="mcp-oauth-table">
                                    <thead>
                                        <tr>
                                            <th>Connection</th>
                                            <th>Session</th>
                                            <th>Freshness</th>
                                            <th>Auth Code</th>
                                            <th>Redirect</th>
                                            <th>Expires</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.sessions.map(s => {
                                            const expiresInMs = new Date(s.challengeExpiry).getTime() - Date.now()
                                            const expiresInSec = Math.max(0, Math.round(expiresInMs / 1000))
                                            return (
                                                <tr key={s.sessionId} className={s.challengeFreshness === 'expired' ? 'mcp-oauth-row-expired' : ''}>
                                                    <td className="mcp-oauth-cell-name">
                                                        {lookupLabel(s.configBlobId)}
                                                    </td>
                                                    <td className="mcp-oauth-cell-session">
                                                        <button className="mcp-oauth-copy-id" onClick={() => handleCopySessionId(s.sessionId)}
                                                            title={copiedId === s.sessionId ? 'Copied!' : 'Click to copy'}>
                                                            {s.sessionId.slice(0, 8)}…
                                                        </button>
                                                    </td>
                                                    <td className="mcp-oauth-cell-freshness">
                                                        <span className={`mcp-oauth-badge mcp-oauth-badge-${s.challengeFreshness}`}>
                                                            <Clock size={11} />
                                                            {s.challengeFreshness}
                                                        </span>
                                                    </td>
                                                    <td className="mcp-oauth-cell-authcode">
                                                        {s.hasAuthCode
                                                            ? <Key size={13} className="mcp-oauth-icon-granted" />
                                                            : <span className="mcp-oauth-badge mcp-oauth-badge-pending">pending</span>}
                                                    </td>
                                                    <td className="mcp-oauth-cell-redirect" title={s.redirectUri}>
                                                        {(() => {
                                                            try { return new URL(s.redirectUri).hostname }
                                                            catch { return s.redirectUri.slice(0, 24) + '…' }
                                                        })()}
                                                    </td>
                                                    <td className="mcp-oauth-cell-expiry">
                                                        {s.challengeFreshness === 'expired' ? '—' : `${expiresInSec}s`}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>,
        document.body,
    )
}
